import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * POST /v2/auth/login (PD-1512) — password login for the provider portal. Returns a
 * challenge state, never Medplum artifacts: no login id, no code, no verifier, and — for
 * web clients — never a refresh credential (the handle is cookie-only). The `success`
 * branch exists for the trusted-device short-circuit; every first login answers `mfa`.
 *
 * Native opt-in (PD-1542): a request declaring `client: 'native'` receives its refresh
 * handle and trust token in the response body instead of cookies (Keychain storage on
 * device). The opt-in is refused for any request carrying an Origin header (browsers
 * always send one on POST), and the flow-separation invariant holds: a body credential
 * is only ever issued where body credentials authenticated the request. Each
 * grant-issuing call (login / profile-select / mfa-verify) declares its client kind —
 * the call that returns `state: 'success'` decides the wire.
 */

/** Native-only body credentials — absent (never present) on web responses. */
const nativeCredentialFields = {
  /** The rotating refresh handle. Store in the platform Keychain, biometric-gated. */
  refreshToken: z.string().optional(),
  /** ISO instant of the session's absolute expiry — when a fresh login is required. */
  refreshTokenExpiresAt: z.string().optional(),
  /** Trusted-device token (rememberDevice) — body twin of the web `bh_mfa_trust` cookie. */
  trustToken: z.string().optional(),
}

export const authLoginSuccessSchema = z.object({
  state: z.literal('success'),
  accessToken: z.string(),
  accessTokenExpiresAt: z.string(),
  profileRef: z.string(),
  /** The profile's formatted name, when readable — the mobile header greets by it (PD-1649). */
  profileDisplay: z.string().optional(),
  sessionKind: z.enum(['provider', 'patient']),
  ...nativeCredentialFields,
})

/** Native opt-in request fields, shared by the grant-issuing endpoints. */
export const nativeClientFields = {
  /** Declares a native app (no cookie jar). Rejected when an Origin header is present. */
  client: z.literal('native').optional(),
  /** Human-recognizable device name for the session listing (e.g. "iPhone 17 Pro"). */
  deviceLabel: z.string().max(64).optional(),
}

const authLoginOutputSchema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('mfa'),
    challengeId: z.string(),
    methods: z.object({ phone: z.boolean(), email: z.boolean() }),
    maskedPhone: z.string().optional(),
    maskedEmail: z.string().optional(),
  }),
  z.object({
    state: z.literal('membershipSelect'),
    challengeId: z.string(),
    memberships: z.array(
      z.object({
        id: z.string(),
        profileRef: z.string().optional(),
        profileDisplay: z.string().optional(),
        /** Project display name — what the picker shows (legacy-flow parity). */
        projectName: z.string().optional(),
      }),
    ),
  }),
  authLoginSuccessSchema,
])

export const authLogin = oc
  .route({ method: 'POST', path: '/v2/auth/login' })
  .input(
    z.object({
      email: z.string().min(1),
      password: z.string().min(1),
      ...nativeClientFields,
      /** Native's body twin of the trust cookie — enables the trusted-device short-circuit. */
      trustToken: z.string().optional(),
    }),
  )
  .output(authLoginOutputSchema)
